import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Globe, Menu, X, ChevronDown, Briefcase, User, Phone, Mail } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { scrollToY } from "./ScrollControls";

const LANGS = [
  { code: "ar", label: "ع" },
  { code: "en", label: "EN" },
  { code: "tr", label: "TR" },
];

export const Logo = () => (
  <Link to="/" data-testid="logo-link" onClick={() => scrollToY(0)} className="flex items-center group shrink-0">
    <div className="h-12 rounded-xl overflow-hidden shadow-md shadow-orange-900/20 group-hover:scale-105 transition-transform">
      <img src="/assets/logo-web.png" alt="Hire Host" className="h-full w-auto object-cover object-left" />
    </div>
  </Link>
);

const Drop = ({ label, items, testid }) => (
  <div className="relative group" data-testid={testid}>
    <button className="flex items-center gap-1 text-sm font-bold text-[#1B2A4E]/75 hover:text-[#E8842C] transition-colors py-2">
      {label} <ChevronDown className="w-3.5 h-3.5 group-hover:rotate-180 transition-transform duration-300" />
    </button>
    <div className="absolute top-full start-0 pt-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible translate-y-2 group-hover:translate-y-0 transition-all duration-300 z-50">
      <div className="bg-white rounded-2xl border border-[#1B2A4E]/10 shadow-xl shadow-[#1B2A4E]/10 py-2 min-w-56 overflow-hidden">
        {items.map((it) => (
          <Link
            key={it.to + it.label}
            to={it.to}
            data-testid={it.testid}
            className="block px-5 py-2.5 text-sm font-bold text-[#1B2A4E]/70 hover:text-[#E8842C] hover:bg-[#E8842C]/5 transition-colors"
          >
            {it.label}
          </Link>
        ))}
      </div>
    </div>
  </div>
);

export default function Header() {
  const { lang, setLang, t } = useLang();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const menus = [
    { label: t("nav.about"), testid: "nav-about", items: [
      { to: "/about", label: t("nav.about_company"), testid: "nav-about-company" },
      { to: "/team", label: t("nav.meet_team"), testid: "nav-meet-team" },
    ]},
    { label: t("nav.employers"), testid: "nav-employers", items: [
      { to: "/employer", label: t("nav.overview"), testid: "nav-employer-overview" },
      { to: "/employer#inquiry", label: t("nav.place_order"), testid: "nav-place-order" },
    ]},
    { label: t("nav.solutions"), testid: "nav-solutions", items: [
      { to: "/solutions", label: t("nav.our_solutions"), testid: "nav-our-solutions" },
      { to: "/solutions#staffing", label: t("nav.staffing_service"), testid: "nav-staffing-service" },
    ]},
    { label: t("nav.jobseekers"), testid: "nav-jobseekers", items: [
      { to: "/employee", label: t("nav.js_overview"), testid: "nav-js-overview" },
      { to: "/employee#jobs", label: t("nav.job_openings"), testid: "nav-job-openings" },
      { to: "/apply", label: t("nav.apply_now"), testid: "nav-apply-now" },
      { to: "/faq", label: t("nav.faq"), testid: "nav-faq" },
      { to: "/#testimonials", label: t("nav.testimonials_menu"), testid: "nav-testimonials" },
    ]},
  ];

  const mobileLinks = [
    { to: "/", label: t("nav.home") },
    { to: "/about", label: t("nav.about_company") },
    { to: "/team", label: t("nav.meet_team") },
    { to: "/employer", label: `${t("nav.employers")} — ${t("nav.overview")}` },
    { to: "/employer#inquiry", label: t("nav.place_order") },
    { to: "/solutions", label: t("nav.our_solutions") },
    { to: "/solutions#staffing", label: t("nav.staffing_service") },
    { to: "/employee", label: `${t("nav.jobseekers")} — ${t("nav.js_overview")}` },
    { to: "/employee#jobs", label: t("nav.job_openings") },
    { to: "/apply", label: t("nav.apply_now") },
    { to: "/faq", label: t("nav.faq") },
    { to: "/contact", label: t("nav.contact") },
  ];

  return (
    <motion.header
      initial={{ y: -100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
      className="fixed top-3 inset-x-3 sm:inset-x-6 z-50"
      data-testid="main-header"
    >
      <div className="hidden md:flex items-center justify-between bg-[#1B2A4E] text-white rounded-t-2xl px-5 py-1.5 text-[11px]">
        <div className="flex items-center gap-5">
          <a href="tel:+919867288688" data-testid="topbar-phone" className="flex items-center gap-1.5 hover:text-[#F5A623] transition-colors"><Phone className="w-3 h-3 text-[#E8842C]" /> +91 98672 88688</a>
          <a href="mailto:hire@hirehost.in" data-testid="topbar-email" className="flex items-center gap-1.5 hover:text-[#F5A623] transition-colors"><Mail className="w-3 h-3 text-[#E8842C]" /> hire@hirehost.in</a>
        </div>
        <div className="flex items-center gap-4">
          <Link to="/employer/login" data-testid="employer-login-btn" className="flex items-center gap-1.5 font-bold hover:text-[#F5A623] transition-colors"><Briefcase className="w-3 h-3" /> {t("nav.employer_login")}</Link>
          <Link to="/employee/login" data-testid="employee-login-btn" className="flex items-center gap-1.5 font-bold hover:text-[#F5A623] transition-colors"><User className="w-3 h-3" /> {t("nav.employee_login")}</Link>
        </div>
      </div>

      <div className="glass rounded-b-2xl md:rounded-t-none rounded-t-2xl px-4 py-2.5 flex items-center justify-between shadow-lg shadow-[#1B2A4E]/5">
        <Logo />

        <nav className="hidden lg:flex items-center gap-6">
          <Link to="/" data-testid="nav-home" onClick={() => scrollToY(0)} className="text-sm font-bold text-[#1B2A4E]/75 hover:text-[#E8842C] transition-colors">{t("nav.home")}</Link>
          {menus.map((m) => <Drop key={m.testid} {...m} />)}
          <Link to="/contact" data-testid="nav-contact" className="text-sm font-bold text-[#1B2A4E]/75 hover:text-[#E8842C] transition-colors">{t("nav.contact")}</Link>
        </nav>

        <div className="flex items-center gap-3">
          <div className="hidden sm:flex items-center gap-1 border border-[#1B2A4E]/15 rounded-full p-1 bg-white" data-testid="language-switcher">
            <Globe className="w-3.5 h-3.5 text-[#E8842C] mx-1.5" />
            {LANGS.map((l) => (
              <button
                key={l.code}
                data-testid={`lang-${l.code}`}
                onClick={() => setLang(l.code)}
                className={`px-2.5 py-1 rounded-full text-xs font-black transition-all ${
                  lang === l.code ? "bg-[#1B2A4E] text-white" : "text-[#1B2A4E]/50 hover:text-[#1B2A4E]"
                }`}
              >
                {l.label}
              </button>
            ))}
          </div>

          <button
            data-testid="header-inquire-cta"
            onClick={() => navigate("/employer#inquiry")}
            className="hidden sm:inline-flex btn-primary !py-2.5 !px-5 text-xs"
          >
            {t("nav.inquire")}
          </button>

          <button data-testid="mobile-menu-btn" onClick={() => setOpen(!open)} className="lg:hidden text-[#1B2A4E] p-1">
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden glass bg-white rounded-2xl mt-2 p-5 max-h-[70vh] overflow-y-auto" data-testid="mobile-menu">
          <div className="flex flex-col gap-3.5">
            {mobileLinks.map((l) => (
              <Link key={l.to + l.label} to={l.to} onClick={() => { setOpen(false); if (l.to === "/") scrollToY(0); }} className="font-bold text-[#1B2A4E] text-sm border-b border-[#1B2A4E]/5 pb-2.5">
                {l.label}
              </Link>
            ))}
            <Link to="/employer/login" onClick={() => setOpen(false)} className="font-bold text-[#E8842C] text-sm">{t("nav.employer_login")}</Link>
            <Link to="/employee/login" onClick={() => setOpen(false)} className="font-bold text-[#E8842C] text-sm">{t("nav.employee_login")}</Link>
            <div className="flex gap-2 pt-1">
              {LANGS.map((l) => (
                <button
                  key={l.code}
                  data-testid={`lang-mobile-${l.code}`}
                  onClick={() => setLang(l.code)}
                  className={`px-3 py-1.5 rounded-full text-xs font-black ${lang === l.code ? "bg-[#1B2A4E] text-white" : "text-[#1B2A4E]/50 border border-[#1B2A4E]/15"}`}
                >
                  {l.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </motion.header>
  );
}
